import { useState } from "react";
import { useQueryClient } from "react-query";
import { EStudentProgressQueryKeys } from "api/endpoints/student-progress";
import {
  getChapterStudentProgress,
  moveHistoriesToClass,
  moveHistoriesToHomework,
} from "api/services/student-progress";
import { logError } from "utils/notifications";
import { TExercise } from "types/api/exercise";
import { TCourseLessonChapter } from "types/api/course";
import { TUrlParam } from "types/utils";

export const useApplyMoveToAllStudents = (
  courseId: TUrlParam,
  lessonId: TUrlParam,
  lessonChapters: TCourseLessonChapter[]
) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const applyToAll = async (
    selectedExerciseIds: number[],
    studentIds: number[],
    homework: boolean
  ) => {
    setIsLoading(true);
    try {
      await Promise.all(
        studentIds.map(async (sid) => {
          const historyIds: number[] = [];
          for (const chapter of lessonChapters) {
            const queryKey = [
              EStudentProgressQueryKeys.getChapterStudentProgress,
              courseId,
              lessonId,
              chapter.id,
              String(sid),
            ];
            let data = queryClient.getQueryData<TExercise[]>(queryKey);
            if (!data) {
              try {
                data = await queryClient.fetchQuery(queryKey, () =>
                  getChapterStudentProgress(
                    courseId,
                    lessonId,
                    String(chapter.id),
                    String(sid)
                  )
                );
              } catch {
                continue;
              }
            }
            const ids = (data ?? [])
              .filter(
                (e) =>
                  e.exerciseId != null &&
                  selectedExerciseIds.includes(e.exerciseId)
              )
              .map((e) => e.id);
            historyIds.push(...ids);
          }
          if (historyIds.length > 0) {
            homework
              ? await moveHistoriesToHomework(
                  String(sid),
                  courseId,
                  lessonId,
                  historyIds
                )
              : await moveHistoriesToClass(
                  String(sid),
                  courseId,
                  lessonId,
                  historyIds
                );
          }
        })
      );
      queryClient.invalidateQueries([
        EStudentProgressQueryKeys.getChapterStudentProgress,
        courseId,
        lessonId,
      ]);
    } catch {
      logError();
      throw new Error("Failed to apply move to all students");
    } finally {
      setIsLoading(false);
    }
  };

  return { applyToAll, isLoading };
};
